import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import UpdatesSection from '../../components/feature/UpdatesSection';
import PageHero from '../../components/shared/PageHero';

const pillars = [
  {
    icon: 'ri-graduation-cap-line',
    title: 'Education & Skills',
    description:
      'Technical scholarships, STEM support for host-community schools, and hands-on apprenticeships that build the next generation of Nigerian energy professionals.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Health & Wellbeing',
    description:
      'Medical outreach, clean-water access, and health-awareness campaigns delivered in partnership with local clinics and NGOs.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Environmental Stewardship',
    description:
      'Responsible waste management, spill-prevention practices, and restoration projects that protect the land and waterways around our operations.',
  },
  {
    icon: 'ri-community-line',
    title: 'Local Content & Enterprise',
    description:
      'Prioritising local hiring, Nigerian suppliers, and SME development to keep value within the communities we serve.',
  },
];

const stats = [
  { value: '70%+', label: 'Local workforce' },
  { value: '120+', label: 'Students supported' },
  { value: '15', label: 'Host communities engaged' },
  { value: '0', label: 'Tolerance for harm' },
];

const initiatives = [
  {
    tag: 'Education',
    title: 'Alphaden Technical Scholars',
    description:
      'An annual scholarship and mentorship programme for engineering and geoscience students from host communities, with industrial placements on live projects.',
    image:
      'https://res.cloudinary.com/dpkn1ppzj/image/upload/v1770029081/Frame_2147226678_1_ygdsew.png',
  },
  {
    tag: 'Environment',
    title: 'Clean Sites Initiative',
    description:
      'Closed-loop solids control and drilling-waste treatment deployed with our OEM partners to cut discharge and reduce environmental footprint at every rig site.',
    image:
      'https://res.cloudinary.com/dpkn1ppzj/image/upload/v1770054006/5ac152e6a20ff71e66276e88800c410bf28e8881_zfwrsi.png',
  },
  {
    tag: 'Community',
    title: 'Community Health Outreach',
    description:
      'Free medical screenings, maternal-health support, and borehole water projects delivered alongside community leaders and local health workers.',
    image:
      'https://res.cloudinary.com/dpkn1ppzj/image/upload/v1770027201/5876d9f779d5215a8b5363d48db7448b3697b9ed_x7m5zk.png',
  },
];

const commitments = [
  'Engage host communities transparently through regular dialogue and memoranda of understanding.',
  'Comply with the Nigerian Oil and Gas Industry Content Development Act and support local capacity building.',
  'Integrate environmental and social impact assessments into every project lifecycle.',
  'Measure, report, and continuously improve the outcomes of our social investment programmes.',
  'Uphold human rights, fair labour practices, and zero tolerance for corruption across our supply chain.',
];

export default function CSRPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <PageHero
        title="Corporate Social Responsibility"
        subtitle="Creating shared value for the people and places where we work."
        image="https://res.cloudinary.com/dpkn1ppzj/image/upload/v1770056664/b517b305ed756a2a6e006ffd1d8b5a500a69ca3a_pv7giv.png"
      />

      {/* Intro */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <p className="text-[#D8242A] font-medium mb-4">Our Commitment</p>
            <h2 className="text-2xl lg:text-[36px] font-medium text-[#0A2540] mb-6 leading-tight">
              Energy that powers progress — for operators and for communities.
            </h2>
            <p className="text-lg text-[#677078] leading-relaxed mb-4">
              As an indigenous Nigerian company, Alphaden Exploration &amp; Production Limited believes that long-term
              success in the upstream sector is inseparable from the wellbeing of our host communities and the
              environment.
            </p>
            <p className="text-lg text-[#677078] leading-relaxed">
              Our CSR programme focuses on education, health, environmental stewardship, and local enterprise —
              building capacity that lasts well beyond the life of any single project.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl p-6 lg:p-8 bg-[#F8FAFC] border border-[#E5EAF0] hover:border-[#0875D4] hover:-translate-y-1 transition-all duration-300"
              >
                <p className="text-3xl lg:text-[40px] font-medium text-[#00458F] mb-2">{stat.value}</p>
                <p className="text-sm text-[#677078]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 lg:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#E37D33] font-medium text-lg">Focus Areas</span>
            <h2 className="text-3xl lg:text-[43px] font-medium text-[#010305] mt-3 mb-3">Our CSR pillars</h2>
            <p className="text-lg text-[#677078] max-w-3xl mx-auto">
              Four areas where we invest our time, expertise, and resources.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((pillar) => (
              <div
                key={pillar.title}
                className="group bg-white p-8 rounded-xl transition-all duration-300 hover:-translate-y-1"
                style={{ boxShadow: '0px 10px 15px -3px #00000014, 0px 4px 6px -4px #00000014' }}
              >
                <div className="w-[56px] h-[56px] bg-[#E4F1FF] rounded-full flex items-center justify-center mb-6 transition-colors duration-300 group-hover:bg-[#00458F]">
                  <i className={`${pillar.icon} text-2xl text-[#00458F] transition-colors duration-300 group-hover:text-white`}></i>
                </div>
                <h3 className="text-xl font-medium text-[#0F172A] mb-3">{pillar.title}</h3>
                <p className="text-[#4B5563] leading-relaxed text-[15px]">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Initiatives */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
            <div>
              <span className="text-[#E37D33] font-medium text-lg">In Action</span>
              <h2 className="text-3xl lg:text-[43px] font-medium text-[#010305] mt-3">Featured initiatives</h2>
            </div>
            <p className="text-lg text-[#677078] max-w-xl">
              Programmes we run with community leaders, partners, and our own people.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {initiatives.map((item) => (
              <article key={item.title} className="group rounded-xl overflow-hidden border border-[#E5EAF0] bg-white hover:shadow-xl transition-shadow duration-300">
                <div className="h-56 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block text-xs font-medium uppercase tracking-wide text-[#0875D4] bg-[#E4F1FF] px-3 py-1 rounded-full mb-4">
                    {item.tag}
                  </span>
                  <h3 className="text-xl font-medium text-[#0F172A] mb-3">{item.title}</h3>
                  <p className="text-[#4B5563] leading-relaxed text-[15px]">{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments + CTA */}
      <section className="grid lg:grid-cols-2">
        <div className="bg-[#00458F] text-white p-12 lg:p-20">
          <h2 className="text-3xl lg:text-[40px] font-medium mb-8">Our pledge</h2>
          <ul className="space-y-4">
            {commitments.map((c) => (
              <li key={c} className="flex gap-4 leading-relaxed text-white/90">
                <i className="ri-checkbox-circle-fill text-xl text-white flex-shrink-0"></i>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-[#333333] text-white p-12 lg:p-20 flex items-center">
          <div className="max-w-[460px]">
            <h2 className="text-3xl lg:text-[40px] font-medium mb-4">Partner with us</h2>
            <p className="leading-relaxed text-white/80 mb-8">
              Are you a community organisation, NGO, or institution working on education, health, or the environment
              in Nigeria? We'd love to explore how we can make an impact together.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#0875D4] hover:bg-[#0663B5] text-white font-medium px-6 py-3 rounded-md transition-colors duration-300"
            >
              Get in touch <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <UpdatesSection bgColor="bg-[#F9F9F9]" />
      <Footer />
    </div>
  );
}
